// Beta readers on the web (PROTOCOL.md §7, design §2.7): rounds of private links to one checkpoint, one link per
// reader, and their notes brought back as comments.
//
// - A round makes (or uses) a checkpoint, builds the copy from it (`makeReviewCopy`: prose only, refused while a
//   fragment's heads haven't reached this computer), and for each reader a fresh review key, the capability's hash on
//   the relay and the copy sealed with that key. The round, with each link's key, is kept in the project document
//   (`settings.webReaderRounds`), so any of the writer's devices can show a link again or open its notes.
// - Placing fetches what the relay hasn't seen placed, opens it with each link's key and hands it to the core's port of
//   `ReviewNotePlacer` (comments at the checkpoint's heads, progress into `settings.readerProgress`), then tells the
//   relay which versions were placed.
import type { Api } from '@writing-suite/client/net/api.ts';
import { fromB64u, toB64u } from '@writing-suite/client/shared/b64.ts';
import { capabilityHash, newReviewKey, openReview, reviewCapability, reviewLink, sealReview } from '@writing-suite/client/shared/review.ts';
import { readProject, type CheckpointRecord } from '@writing-suite/core';
import {
  decodeReaderProgress, decodeReviewNote, encodeReviewCopy, makeReviewCopy, placeReviewNotes, putWebRound, storedProgress, webLinks, webRounds,
  type IncomingNote, type IncomingProgress, type PlaceResult, type WebReader, type WebReaderRound,
} from '@writing-suite/core/review';
import type { LiveProject } from './project.ts';

/** A link as the relay reports it (`GET /api/reviews`). */
export interface LinkStatus {
  id: string;
  round: string | null;
  createdAt: number;
  expiresAt: number | null;
  revokedAt: number | null;
  openedAt: number | null;
  lastOpenedAt: number | null;
  lastActivityAt: number | null;
  notes: number;
  unplaced: number;
  progressAt: number | null;
  progressWithdrawn: boolean;
  copySize: number | null;
}

export interface NewRound {
  name: string;
  /** An existing checkpoint, or null to make one now (named after the round). */
  checkpointId: string | null;
  readers: string[];
  returnBy: Date | null;
  message: string;
  /** A hard expiry for every link (the relay answers 410 after it). */
  expiresAt: Date | null;
  /** Who the copy says it's from. */
  from: string;
}

const lowerUUID = () => crypto.randomUUID().toLowerCase();
/** Now, to the second: what the project keeps (ISO 8601 without fractions), so a round reads back as it was made. */
const nowSeconds = () => new Date(Math.floor(Date.now() / 1000) * 1000);

export class ReaderLinks {
  constructor(
    private readonly api: Api,
    private readonly live: LiveProject,
  ) {}

  private project() {
    const doc = this.live.doc('project');
    if (!doc) throw new Error('The project isn’t open yet.');
    return doc;
  }

  rounds(): WebReaderRound[] {
    const doc = this.live.doc('project');
    return doc ? webRounds(doc) : [];
  }

  /** Progress kept with the project (placed), per link: review id → fraction (0–1). */
  progress(): Map<string, number> {
    const doc = this.live.doc('project');
    const out = new Map<string, number>();
    if (!doc) return out;
    const links = webLinks(doc);
    for (const p of storedProgress(doc)) {
      const link = links.get(p.reviewID);
      if (link && (p.readerID === undefined || p.readerID === link.reader.id) && !out.has(p.reviewID)) out.set(p.reviewID, p.fraction);
    }
    return out;
  }

  async status(): Promise<{ readOrigin: string | null; links: Map<string, LinkStatus> }> {
    const r = await this.api.get<{ readOrigin: string | null; reviews: LinkStatus[] }>(`/api/reviews?project=${encodeURIComponent(this.live.id)}`);
    return { readOrigin: r.readOrigin, links: new Map(r.reviews.map((x) => [x.id, x])) };
  }

  static link(readOrigin: string, reader: WebReader): string {
    return reviewLink(readOrigin, reader.reviewID, fromB64u(reader.key));
  }

  /** Makes a round: the checkpoint (if asked), then each reader's link and sealed copy, then the round in the project. */
  async createRound(o: NewRound, onProgress: (text: string) => void = () => {}): Promise<{ round: WebReaderRound; readOrigin: string }> {
    const names = o.readers.map((n) => n.trim()).filter(Boolean);
    if (!names.length) throw new Error('Add at least one reader.');
    const name = o.name.trim() || 'Readers';
    let checkpointId = o.checkpointId;
    if (!checkpointId) {
      onProgress('Making a checkpoint…');
      checkpointId = this.live.edit((e) => e.createCheckpoint(name).id);
    }
    const checkpoint = readProject(this.project()).checkpoints.find((c) => c.id === checkpointId);
    if (!checkpoint) throw new Error('That checkpoint isn’t on this computer.');
    const round: WebReaderRound = {
      id: lowerUUID(), name, checkpointID: checkpoint.id, createdAt: nowSeconds(), message: o.message.trim(), readers: [],
      ...(o.returnBy ? { returnBy: o.returnBy } : {}),
    };
    let readOrigin = '';
    for (const [i, readerName] of names.entries()) {
      onProgress(`Making ${readerName}’s link (${i + 1} of ${names.length})…`);
      const reader = await this.makeLink(round, checkpoint, readerName, o);
      readOrigin = reader.readOrigin;
      round.readers.push(reader.reader);
      // Kept after each link, so a failure part-way loses none that were made.
      this.live.editDocuments((docs) => putWebRound(docs, round));
    }
    onProgress('');
    return { round, readOrigin };
  }

  private async makeLink(round: WebReaderRound, checkpoint: CheckpointRecord, readerName: string, o: NewRound) {
    const reviewId = lowerUUID();
    const key = newReviewKey();
    const copy = makeReviewCopy(this.live.documents(), checkpoint, {
      id: reviewId, from: o.from, message: o.message.trim(), reader: readerName, notesBy: o.returnBy,
    });
    const made = await this.api.call<{ id: string; readOrigin: string }>('POST', '/api/reviews', {
      id: reviewId,
      projectId: this.live.id,
      capabilityHash: await capabilityHash(await reviewCapability(key)),
      round: round.id,
      ...(o.expiresAt ? { expiresAt: o.expiresAt.getTime() } : {}),
    });
    const sealed = await sealReview(key, reviewId, 'copy', encodeReviewCopy(copy));
    await this.putCopy(reviewId, sealed);
    const reader: WebReader = {
      id: lowerUUID(), name: readerName, format: 'web', reviewID: reviewId, key: toB64u(key), ...(o.expiresAt ? { expiresAt: o.expiresAt } : {}),
    };
    return { reader, readOrigin: made.readOrigin };
  }

  private async putCopy(reviewId: string, sealed: Uint8Array) {
    const send = async () =>
      fetch(`${this.api.origin}/api/reviews/${reviewId}/copy`, {
        method: 'PUT',
        headers: { authorization: `Bearer ${await this.api.access()}`, 'content-type': 'application/octet-stream' },
        body: sealed as Uint8Array<ArrayBuffer>,
        credentials: 'same-origin',
      });
    let res = await send();
    if (res.status === 401) {
      await this.api.refresh();
      res = await send();
    }
    if (!res.ok) throw new Error(`The copy couldn’t be uploaded (${res.status}).`);
  }

  /** Withdraws a link: the relay deletes its copy, notes and progress; the round records when. Placed notes stay. */
  async revoke(roundId: string, reviewId: string): Promise<void> {
    await this.api.call('DELETE', `/api/reviews/${reviewId}`);
    const round = this.rounds().find((r) => r.id === roundId);
    if (!round) return;
    const next = { ...round, readers: round.readers.map((r) => (r.reviewID === reviewId ? { ...r, revokedAt: nowSeconds() } : r)) };
    this.live.editDocuments((docs) => putWebRound(docs, next));
  }

  /**
   * Places what readers sent that the relay hasn't seen placed: notes as comments at the checkpoint's heads, progress
   * into the project. A note whose review, checkpoint or fragment isn't here yet stays unplaced for next time.
   */
  async place(): Promise<PlaceResult & { opened: number }> {
    const empty = { added: 0, updated: 0, handled: [], waiting: [], progressHandled: [], changed: [], failures: [], opened: 0 };
    const project = this.live.doc('project');
    if (!project || !this.live.writable) return empty;
    const links = webLinks(project);
    if (!links.size) return empty;
    const inbox = await this.api.get<{
      reviews: { id: string; notes: { id: string; sealed: string; updatedAt: number }[]; progress: { sealed?: string; withdrawn?: boolean; updatedAt: number } | null }[];
    }>(`/api/reviews/inbox?project=${encodeURIComponent(this.live.id)}`);
    const notes: IncomingNote[] = [];
    const progress: IncomingProgress[] = [];
    const versions = new Map<string, { reviewId: string; updatedAt: number }>();
    const unreadable: { reviewId: string; id: string; updatedAt: number }[] = [];
    for (const review of inbox.reviews) {
      const link = links.get(review.id);
      if (!link) continue; // a round not on this computer yet
      const key = fromB64u(link.reader.key);
      for (const n of review.notes) {
        try {
          const note = decodeReviewNote(await openReview(key, review.id, 'note', fromB64u(n.sealed)));
          if (note.id !== n.id) throw new Error('note id mismatch');
          notes.push({ reviewId: review.id, note });
          versions.set(n.id, { reviewId: review.id, updatedAt: n.updatedAt });
        } catch {
          unreadable.push({ reviewId: review.id, id: n.id, updatedAt: n.updatedAt });
        }
      }
      if (review.progress?.withdrawn) progress.push({ reviewId: review.id, progress: null, withdrawn: true });
      else if (review.progress?.sealed) {
        try {
          progress.push({ reviewId: review.id, progress: decodeReaderProgress(await openReview(key, review.id, 'progress', fromB64u(review.progress.sealed))) });
        } catch {
          // unreadable: marked seen below
        }
      }
    }
    const reviews = [...links.values()].map(({ round, reader }) => ({ reviewId: reader.reviewID, checkpointID: round.checkpointID, readerID: reader.id }));
    const placed = notes.length || progress.length ? this.live.editDocuments((docs) => placeReviewNotes(docs, { reviews, notes, progress })) : { ...empty };
    // Tell the relay which versions were dealt with (placed, already there, or unplaceable junk), per review.
    const done = new Map<string, { notes: { id: string; updatedAt: number }[]; progressAt?: number }>();
    const entry = (rid: string) => done.get(rid) ?? (done.set(rid, { notes: [] }), done.get(rid)!);
    for (const id of placed.handled) {
      const v = versions.get(id);
      if (v) entry(v.reviewId).notes.push({ id, updatedAt: v.updatedAt });
    }
    for (const u of unreadable) entry(u.reviewId).notes.push({ id: u.id, updatedAt: u.updatedAt });
    for (const review of inbox.reviews) {
      if (review.progress && links.has(review.id) && (placed.progressHandled.includes(review.id) || !progress.some((p) => p.reviewId === review.id))) {
        entry(review.id).progressAt = review.progress.updatedAt;
      }
    }
    // The comments are batched and stored on this computer (sealed, sent as the connection allows) before the relay
    // is told they're placed.
    if (done.size) await this.live.flush();
    for (const [rid, body] of done) await this.api.call('POST', `/api/reviews/${rid}/placed`, body);
    return { ...placed, opened: notes.length };
  }
}
